// @mentions (docs/specs/messaging-notifications-spec.md): the composer's list, the `mentions` a
// send carries, and their highlighting in the thread.
//
// A message keeps the visible "@Name" text; who it names goes alongside, by id ({user_ids, here,
// channel}), because names aren't unique and the server only trusts ids from the composer's list.
// @here / @channel / @everyone typed in the text count on the server even without the list.

export const BROADCASTS = [
  { id: "here", label: "@here", hint: "Notify everyone online in this channel" },
  { id: "channel", label: "@channel", hint: "Notify everyone in this channel" },
];

const MAX_SUGGESTIONS = 8;
const WORD_CHAR = /[\p{L}\p{N}_]/u;

// The "@que" being typed just before the caret: { query, start } (start = the "@"'s offset in
// `before`), or null. "@" must start the text or follow a space or an opening bracket, so an email
// address never opens the list. The query is one word; a space ends it.
export function findMentionQuery(before) {
  const m = /(?:^|[\s(])@([^\s@]{0,30})$/u.exec(String(before || ""));
  if (!m) return null;
  return { query: m[1], start: m.index + m[0].length - m[1].length - 1 };
}

// The list for a query: members whose name has a word starting with it, then @here / @channel
// (never in a DM, where the server ignores them anyway). Items: { kind: "user"|"broadcast", id, label }.
export function mentionSuggestions(members, query, { isDm = false } = {}) {
  const q = String(query || "").toLowerCase();
  const seen = new Set();
  const users = [];
  (members || []).forEach((m) => {
    const name = String(m?.name || "").trim();
    if (!m?.user_id || !name || seen.has(m.user_id)) return;
    const words = name.toLowerCase().split(/\s+/);
    if (q && !name.toLowerCase().startsWith(q) && !words.some((w) => w.startsWith(q))) return;
    seen.add(m.user_id);
    users.push({ kind: "user", id: m.user_id, label: name });
  });
  users.sort((a, b) => a.label.localeCompare(b.label));
  const broadcasts = isDm
    ? []
    : BROADCASTS.filter((b) => b.id.startsWith(q)).map((b) => ({ kind: "broadcast", id: b.id, label: b.label.slice(1), hint: b.hint }));
  return [...users.slice(0, MAX_SUGGESTIONS), ...broadcasts];
}

function escapeRegExp(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Whether "@<label>" stands in `text` as a whole mention: not glued to a word before it, and not
// running on into one after it ("@Amy" is not in "@Amyx").
function hasMention(text, label) {
  const re = new RegExp(`(^|[^\\p{L}\\p{N}_@.])@${escapeRegExp(label)}(?![\\p{L}\\p{N}_])`, "iu");
  return re.test(text);
}

// What a send carries: the picked people whose "@Name" is still in the text (one deleted after
// picking is not notified), and @here / @channel / @everyone as typed. null when it names no one.
// `picked`: Map user_id → name, from the composer's list.
export function buildMentionsPayload(text, picked, { isDm = false } = {}) {
  const plain = String(text || "");
  const userIds = [];
  (picked ? Array.from(picked.entries()) : []).forEach(([id, name]) => {
    if (id && name && hasMention(plain, name) && !userIds.includes(id)) userIds.push(id);
  });
  const here = !isDm && hasMention(plain, "here");
  const channel = !isDm && (hasMention(plain, "channel") || hasMention(plain, "everyone"));
  if (!userIds.length && !here && !channel) return null;
  return { user_ids: userIds, here, channel };
}

// What to highlight in a stored message: [{ text: "@Name", self }]. `mentions` is the message's
// ({users, here, channel}); names come from `nameById` (the thread's members and me). `self` marks
// what names the reader: their own @Name, and @here / @channel in someone else's message.
export function mentionTerms(message, nameById, currentUserId) {
  const mentions = message?.mentions;
  if (!mentions || typeof mentions !== "object") return [];
  const ownMessage = message.sender_id === currentUserId;
  const terms = [];
  (Array.isArray(mentions.users) ? mentions.users : []).forEach((id) => {
    const name = nameById?.[id];
    if (name) terms.push({ text: `@${name}`, self: id === currentUserId });
  });
  if (mentions.here) terms.push({ text: "@here", self: !ownMessage });
  if (mentions.channel) {
    terms.push({ text: "@channel", self: !ownMessage });
    terms.push({ text: "@everyone", self: !ownMessage });
  }
  return terms;
}

function termsRegExp(terms) {
  const texts = Array.from(new Set(terms.map((t) => t.text))).sort((a, b) => b.length - a.length);
  if (!texts.length) return null;
  return new RegExp(`(${texts.map(escapeRegExp).join("|")})(?![\\p{L}\\p{N}_])`, "giu");
}

// `text` cut into [{ text, mention?: {self} }] around the terms, for plain-text messages (rendered
// as React text, never as HTML).
export function splitMentions(text, terms) {
  const source = String(text || "");
  const re = termsRegExp(terms || []);
  if (!re || !source) return [{ text: source }];
  const selfOf = new Map();
  terms.forEach((t) => selfOf.set(t.text.toLowerCase(), (selfOf.get(t.text.toLowerCase()) || false) || t.self));
  const out = [];
  let last = 0;
  let m;
  while ((m = re.exec(source)) !== null) {
    const prev = m.index > 0 ? source[m.index - 1] : "";
    if (prev && (WORD_CHAR.test(prev) || prev === "@" || prev === ".")) continue;
    if (m.index > last) out.push({ text: source.slice(last, m.index) });
    out.push({ text: m[0], mention: { self: !!selfOf.get(m[0].toLowerCase()) } });
    last = m.index + m[0].length;
  }
  if (last < source.length) out.push({ text: source.slice(last) });
  return out;
}

// Sanitized message HTML with the terms wrapped in <span data-mention="self|other">. Works on the
// parsed tree's text nodes: the new spans are made with createElement and filled with textContent,
// so a name (or anything in the message) can never become markup. Text inside links and code is
// left alone.
export function highlightMentionsHtml(safeHtml, terms) {
  if (!terms || !terms.length || !safeHtml) return safeHtml;
  const template = document.createElement("template");
  template.innerHTML = safeHtml;
  const root = template.content;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!node.parentElement?.closest?.("a, code")) nodes.push(node);
  }
  let changed = false;
  nodes.forEach((node) => {
    const parts = splitMentions(node.nodeValue, terms);
    if (parts.length === 1 && !parts[0].mention) return;
    const frag = document.createDocumentFragment();
    parts.forEach((part) => {
      if (!part.mention) {
        frag.appendChild(document.createTextNode(part.text));
        return;
      }
      const span = document.createElement("span");
      span.setAttribute("data-mention", part.mention.self ? "self" : "other");
      span.textContent = part.text;
      frag.appendChild(span);
    });
    node.parentNode.replaceChild(frag, node);
    changed = true;
  });
  if (!changed) return safeHtml;
  const holder = document.createElement("div");
  holder.appendChild(root);
  return holder.innerHTML;
}
